/**
 * UI strings and locale direction. Hand-written messages (src/messages/{en,ar}.json) are bundled;
 * entity, field and navigation labels arrive already localised from the API, so the catalogues
 * stay small. `t('common.save')`, `t('home.welcome', { name })`.
 */
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import en from '../../messages/en.json';
import ar from '../../messages/ar.json';
import { formatDate, formatDateTime, formatMoney, formatQuantity, formatCompact, direction, type Locale } from '../../core/i18n/format.ts';

export type { Locale };
type Messages = Record<string, unknown>;
const MESSAGES: Record<Locale, Messages> = { en: en as Messages, ar: ar as Messages };

function lookup(m: Messages, key: string): string | undefined {
  let cur: unknown = m;
  for (const part of key.split('.')) {
    if (cur && typeof cur === 'object' && part in (cur as Messages)) cur = (cur as Messages)[part]; else return undefined;
  }
  return typeof cur === 'string' ? cur : undefined;
}

export function translate(locale: Locale, key: string, vars: Record<string, string | number> = {}): string {
  const raw = lookup(MESSAGES[locale], key) ?? lookup(MESSAGES.en, key) ?? key.split('.').pop()!.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
  return raw.replace(/\{(\w+)\}/g, (_, k: string) => (vars[k] === undefined ? `{${k}}` : String(vars[k])));
}

interface I18n {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  t: (key: string, vars?: Record<string, string | number>) => string;
  /** pick the localised value of a pair coming from the API */
  pick: (en: string | null | undefined, ar?: string | null) => string;
  money: (v: unknown, currency?: string | null, opts?: { compact?: boolean; showCurrency?: boolean; decimals?: number }) => string;
  qty: (v: unknown, decimals?: number) => string;
  date: (v: unknown) => string;
  dateTime: (v: unknown) => string;
}

const I18nContext = createContext<I18n | null>(null);

export function I18nProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  const t = useCallback((key: string, vars?: Record<string, string | number>) => translate(locale, key, vars), [locale]);
  const value = useMemo<I18n>(() => ({
    locale, dir: direction(locale), t,
    pick: (e, a) => (locale === 'ar' && a ? a : e ?? ''),
    money: (v, currency, opts = {}) => v === null || v === undefined || v === '' ? '—' : opts.compact ? formatCompact(String(v), { currency: currency ?? 'EGP', locale }) : formatMoney(String(v), { currency: currency ?? 'EGP', locale, showCurrency: opts.showCurrency ?? true, decimals: opts.decimals ?? 2 }),
    qty: (v, decimals = 2) => v === null || v === undefined || v === '' ? '—' : formatQuantity(String(v), decimals, locale),
    date: (v) => formatDate(v as string | null, locale),
    dateTime: (v) => formatDateTime(v as string | null, locale),
  }), [locale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const v = useContext(I18nContext);
  if (!v) throw new Error('useI18n outside I18nProvider');
  return v;
}
